<template>
    <modal
        ref="fulfillmentModal"
        :adaptive="true"
        :clickToClose="false"
        :maxWidth="575"
        :pivotY="0.5"
        :reset="true"
        :scrollable="true"
        height="auto"
        name="fulfillment-method-modal"
        width="575"
    >
        <div
            class="item-modal-content bg-primary relative flex flex-col w-full"
        >
            <div
                class="item-modal-details flex-auto p-6 overflow-y-auto sm:p-8"
            >
                <div v-if="!showPostcode">
                    <h4
                        class="pb-1 text-heading font-heading text-xl font-bold"
                    >
                        {{ $t('fulfillmentMethodModal.chooseDeliveryMethod') }}
                    </h4>
                </div>
                <div v-else>
                    <h4
                        class="pb-1 text-heading font-heading text-xl font-bold"
                    >
                        {{ $t('fulfillmentMethodModal.deliveryAddress') }}
                    </h4>
                    <p
                        v-if="hasDeliveryZones && venueMaxDistanceInCountryUnit"
                        class="text-subheading leading-relaxed"
                    >
                        {{ deliveryDistanceText }}
                    </p>
                    <div
                        :class="{ spinner: isLoading }"
                        class="relative my-4 w-full"
                    >
                        <autocomplete
                            v-if="hasAutocomplete"
                            @onChange="resetDistance"
                        />
                        <input
                            v-else
                            id="postCode"
                            :value="postCode"
                            autocomplete="shipping postal-code"
                            class="postcode-input block w-full rounded border px-4 pt-3 text-sm text-gray-800 shadow-sm"
                            name="postCode"
                            required
                            style="height: 50px"
                            type="text"
                            @change="resetDistance"
                            @input="
                                postCode = $event.target.value
                                    .toUpperCase()
                                    .trim()
                            "
                        />
                        <label
                            v-if="!hasAutocomplete"
                            class="sk-form-label postcode-input-label pointer-events-none absolute select-none rounded px-4 pt-1"
                            for="postCode"
                        >
                            {{
                                $t('fulfillmentMethodModal.enterPostCode', {
                                    value: venuePostCodeExample
                                })
                            }}
                        </label>

                        <span
                            v-if="hasError"
                            class="block -mb-3 mt-3 text-red-800 text-xs font-bold"
                            v-html="errorMessage"
                        >
                        </span>
                    </div>
                </div>
            </div>
            <div
                v-if="!showPostcode"
                class="item-modal-footer flex flex-col-reverse items-start justify-between p-10 md:flex-row"
            >
                <button
                    v-if="$store.state.venue.venue.acceptsPickup"
                    :class="{ spinner: isLoading }"
                    aria-label="Select pickup fulfillment method"
                    class="no-highlight flex-auto p-3 px-10 w-full text-center text-primary text-sm font-bold border border-button-primary font-button clip-path-button rounded-button rounded-sm focus:opacity-75 active:opacity-75 select-none md:w-auto"
                    tabindex="0"
                    @click="
                        setFulfillmentMethodWithDelay(
                            FulfillmentMethodsEnum.PICKUP
                        )
                    "
                >
                    <span :class="{ 'opacity-0': isLoading }">
                        {{ $t('fulfillmentMethodModal.pickup') }}
                    </span>
                </button>
                <button
                    v-if="hasDeliveryZones && !isDeliveryOnly"
                    :class="{
                        'md:ml-6': $store.state.venue.venue.acceptsPickup,
                        'opacity-60': isLoading
                    }"
                    aria-label="Select delivery fulfillment method"
                    class="no-highlight flex-auto mb-3 p-3 px-12 w-full text-center text-button text-sm font-bold bg-button border border-button-primary font-button clip-path-button rounded-button rounded-sm focus:opacity-75 active:opacity-75 select-none md:mb-0 md:w-auto"
                    tabindex="0"
                    @click="showPostcode = !showPostcode"
                >
                    {{ $t('fulfillmentMethodModal.delivery') }}
                </button>
                <button
                    v-else-if="
                        $store.state.venue.venue.acceptsDelivery &&
                        !isDeliveryOnly
                    "
                    :class="{ spinner: isLoading }"
                    class="no-highlight flex-auto mb-3 p-3 px-12 w-full text-center text-button text-sm font-bold bg-button border border-button-primary font-button clip-path-button rounded-button rounded-sm focus:opacity-75 active:opacity-75 select-none md:mb-0 md:ml-6 md:w-auto"
                    @click="
                        setFulfillmentMethodWithDelay(
                            FulfillmentMethodsEnum.DELIVERY
                        )
                    "
                >
                    <span :class="{ 'opacity-0': isLoading }">
                        {{ $t('fulfillmentMethodModal.delivery') }}
                    </span>
                </button>
            </div>
            <div v-else>
                <div
                    :class="[
                        isDeliveryOnly ? 'justify-center' : 'justify-between'
                    ]"
                    class="item-modal-footer flex flex-col-reverse p-10 md:flex-row"
                >
                    <button
                        v-if="!isDeliveryOnly"
                        :class="{ 'opacity-60': isLoading }"
                        class="no-highlight flex-auto p-3 px-8 w-full text-center text-primary text-sm font-bold border border-button-primary font-button clip-path-button rounded-button rounded-sm select-none md:w-auto"
                        @click="showPostcode = !showPostcode"
                    >
                        {{ $t('fulfillmentMethodModal.goBack') }}
                    </button>

                    <button
                        :class="{
                            'disabled-button': distanceIsTooFar,
                            'md:ml-6': !isDeliveryOnly,
                            'spinner ': isLoading
                        }"
                        :disabled="distanceIsTooFar"
                        class="no-highlight flex-auto mb-3 p-3 px-12 w-full text-center text-button text-sm font-bold bg-button border border-button-primary font-button clip-path-button rounded-button rounded-sm select-none md:mb-0 md:w-auto"
                        @click="getDistance()"
                    >
                        <span :class="{ 'opacity-0': isLoading }">
                            {{ $t('fulfillmentMethodModal.saveAddress') }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import isMobile from '@/helpers/isMobileDevice';
import { FulfillmentMethodsEnum } from '@/enums';
import { helpers, requiredIf } from 'vuelidate/lib/validators';
import Autocomplete from '@/components/formElements/Autocomplete';
import { mapMutations, mapState, mapGetters, mapActions } from 'vuex';
import {
    convertMilesToCountryUnit,
    countryUsesMiles
} from '@/util/distanceHelper';
import { getDeliveryFee } from '@/services/delivery-fee.service';

// https://ideal-postcodes.co.uk/guides/postcode-validation#simpleregularexpression
const postcodeRegex = helpers.regex(
    'alpha',
    /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i
);
const italianPostcodeRegex = helpers.regex('numeric', /^\d{5}$/);
export default {
    name: 'fulfillment-method-modal',
    components: { Autocomplete },
    data() {
        return {
            isLoading: false,
            showPostcode: false,
            distance: null,
            distanceIsTooFar: false,
            errorMessage: null,
            hasError: false,
            FulfillmentMethodsEnum
        };
    },
    validations() {
        return {
            postCode: {
                required: requiredIf(function () {
                    return this.country === 'GB' || this.country === 'IT';
                }),
                ...(this.country === 'GB' && !this.hasAutocomplete
                    ? { postcodeRegex }
                    : this.country === 'IT' && !this.hasAutocomplete
                      ? { italianPostcodeRegex }
                      : false)
            }
        };
    },
    computed: {
        ...mapState('venue', {
            country: state => state.venue.address.country
        }),
        ...mapGetters({
            deliveryAddress: 'deliveryAddress/address',
            formattedAddress: 'deliveryAddress/formattedAddress',
            venueValue: 'venue/venueValue',
            fulfillmentMethod: 'cart/fulfillmentMethod'
        }),
        pivotY() {
            return isMobile() ? 1 : 0.5;
        },
        venueId() {
            return this.venueValue('id');
        },
        venuePostcode() {
            if (!this.$store.state.venue.venue.address) {
                return false;
            }

            return this.$store.state.venue.venue.address.postCode;
        },
        venuePostCodeExample() {
            return this.$store.state.venue.venue.address.postCode || 'SW1A 2AA';
        },
        hasDeliveryZones() {
            return !!(
                this.$store.state.venue.venue.deliveryZones &&
                this.$store.state.venue.venue.deliveryZones.length > 0
            );
        },
        deliveryZones() {
            if (!this.hasDeliveryZones) {
                return null;
            }

            return this.$store.state.venue.venue.deliveryZones;
        },
        venueMaxDistance() {
            if (!this.hasDeliveryZones) {
                return null;
            }

            return Math.max.apply(
                Math,
                this.$store.state.venue.venue.deliveryZones.map(
                    zone => zone.radius
                )
            );
        },
        venueMaxDistanceInCountryUnit() {
            if (!this.venueMaxDistance) {
                return null;
            }

            return this.convertMilesToCountryUnit(
                this.venueMaxDistance,
                this.country
            );
        },
        isDeliveryOnly() {
            return (
                this.$store.state.venue.venue.acceptsDelivery &&
                !this.$store.state.venue.venue.acceptsPickup
            );
        },
        postCode: {
            get() {
                return this.deliveryAddress.postCode;
            },
            set(value) {
                this.setPostCode(value);
            }
        },
        coordinates: {
            get() {
                return this.deliveryAddress.coordinates;
            }
        },
        showMap() {
            return !!(this.coordinates.lat && this.coordinates.lng);
        },
        hasAutocomplete() {
            if (this.country !== 'GB') {
                return true;
            }
            return this.$growthbook.isOn('address_autocomplete');
        },
        deliveryDistanceText() {
            if (!this.venueMaxDistanceInCountryUnit) {
                return '';
            }

            const value = Number.isInteger(this.venueMaxDistanceInCountryUnit)
                ? this.venueMaxDistanceInCountryUnit.toString()
                : this.venueMaxDistanceInCountryUnit.toFixed(1);

            const key = countryUsesMiles(this.country)
                ? 'fulfillmentMethodModal.deliveryMaxMiles'
                : 'fulfillmentMethodModal.deliveryMaxKilometers';

            return this.$t(key, { value });
        }
    },
    watch: {
        isDeliveryOnly(value) {
            this.showPostcode = value;
        }
    },
    methods: {
        ...mapActions({
            setPostCode: 'deliveryAddress/setPostCode'
        }),
        ...mapMutations({
            setFulfillmentMethod: 'cart/setFulfillmentMethod',
            resetPickupAndPreOrderDates: 'cart/RESET_PICKUP_AND_PRE_ORDER_DATES'
        }),
        setFulfillmentMethodWithDelay(value) {
            this.isLoading = true;

            if (value === FulfillmentMethodsEnum.PICKUP) {
                this.$store.commit('deliveryAddress/clearAddress');
            }

            setTimeout(() => {
                this.$store.commit('cart/setDeliveryTime', 0);
                this.setFulfillmentMethod(value);
                this.$store.commit('cart/CLEAR_ZONAL_DISCOUNT');
                this.resetPickupAndPreOrderDates();
                this.isLoading = false;
                this.hideFulfillmentModal();
            }, 100);
        },
        hideFulfillmentModal() {
            this.$modal.hide('fulfillment-method-modal');
        },
        resetDistance() {
            this.hasError = false;
            this.errorMessage = null;
            this.distanceIsTooFar = false;
        },
        async geoDistanceV2(params) {
            const { data } = await getDeliveryFee(this.venueId, params);

            if (!data || data === 'ZERO_RESULTS') {
                this.distanceIsTooFar = true;
                this.hasError = true;
                this.errorMessage = this.$t(
                    'fulfillmentMethodModal.errors.outOfRange'
                );

                // @TODO Manjaros
                if (
                    this.venueId === 7700 ||
                    this.venueId === 7427 ||
                    this.venueId === 6825
                ) {
                    this.errorMessage = `This store does not deliver to that postcode. Find your nearest Manjaros that serves your area. <a href="https://www.scribblemaps.com/maps/view/Manjaros-Delivery-Zones/xFbWwuOLpo">Click Here - Manjaros Delivery Zone Map</a>`;
                }

                // @TODO Manjaros
                if (this.venueId === 8288) {
                    this.errorMessage = `Sorry we don't deliver to this area yet, please order from us via <a href="https://www.ubereats.com/gb/store/manjaros-brick-lane/S_V8MgalXViK2oipbmEuJw?srsltid=AfmBOoqgejeI4XT4q-KNDIMt3liv94GPtLok6dvzr_NpeSJVkuiK2Ipo">Uber Eats (Click Here)</a>`;
                }
                // @TODO Manjaros Ilford
                if (this.venueId === 8276) {
                    this.errorMessage = `Sorry we don't deliver to this area yet, please order from us via <a href="https://www.ubereats.com/gb/store/manjaros-ilford/Hk7KH0vkSVq7FxVNQgRpNA">Uber Eats (Click Here)</a>`;
                }
                // @TODO Manjaros Leyton
                if (this.venueId === 8289) {
                    this.errorMessage = `Sorry we don't deliver to this area yet, please order from us via <a href="https://www.ubereats.com/gb/store/manjaros-leyton/uQe3sJGhUdG9PsiuCKF_hA">Uber Eats (Click Here)</a>`;
                }

                return;
            }

            this.distanceIsTooFar = false;
            this.setFulfillmentMethod(FulfillmentMethodsEnum.DELIVERY);
            this.resetPickupAndPreOrderDates();

            this.$store.commit(
                'cart/setDeliveryTime',
                parseInt(data.duration, 10)
            );
            this.$store.commit('cart/setDeliveryFeeFromZone', data.deliveryFee);
            this.$store.commit(
                'cart/SET_ZONE_DELIVERY_MIN_COST',
                data.minDeliveryAmount
            );

            this.hideFulfillmentModal();
        },
        async getDistance() {
            this.$v.postCode.$touch();

            if (this.$v.$invalid) {
                this.errorMessage = this.$t(
                    'fulfillmentMethodModal.errors.notValidPostcode'
                );
                this.hasError = true;

                return false;
            }

            this.isLoading = true;

            const hasValidCoordinates = !!(
                this.deliveryAddress.coordinates &&
                this.deliveryAddress.coordinates.lat &&
                this.deliveryAddress.coordinates.lat
            );

            let destinations;
            if (hasValidCoordinates) {
                destinations = `${this.deliveryAddress.coordinates.lat},${this.deliveryAddress.coordinates.lng}`;
            } else {
                const addressParts = [
                    this.deliveryAddress.street,
                    this.deliveryAddress.city,
                    this.postCode,
                    this.country
                ].filter(part => part && part.trim());

                destinations = addressParts.join(', ');
            }

            let params = {
                origins: `${this.$store.state.venue.venue.address.street}, ${this.$store.state.venue.venue.address.city}, ${this.venuePostcode}, ${this.$store.state.venue.venue.address.country}`,
                destinations,
                units: 'imperial',
                departure_time: 'now',
                hasAutocomplete: hasValidCoordinates,
                postCode: this.postCode,
                country: this.country
            };

            try {
                await this.geoDistanceV2(params);
            } catch (e) {
                this.hasError = true;
                throw new Error(`API ${e}`);
            } finally {
                this.isLoading = false;
            }
        },
        convertMilesToCountryUnit
    }
};
</script>

<style>
.v--modal-overlay[data-modal='fulfillment-method-modal'] {
    background: rgba(0, 0, 0, 0.5);
}

.v--modal[data-modal='fulfillment-method-modal'],
.v--modal-box[data-modal='fulfillment-method-modal'] {
    max-height: 100dvh;
}
</style>

<style scoped src="@/assets/css/fulfillment-method-modal_scoped.css"></style>
